import { useLayoutEffect, useState, type RefObject } from 'react'

function findScrollParent(element: HTMLElement | null): HTMLElement {
  let node = element?.parentElement ?? null
  while (node) {
    const { overflowY } = window.getComputedStyle(node)
    if (/(auto|scroll|overlay)/.test(overflowY)) {
      return node
    }
    node = node.parentElement
  }
  return document.documentElement
}

function measureStickyPanelMaxHeight(panelEl: HTMLElement, scrollParent: HTMLElement): number {
  const parentRect = scrollParent.getBoundingClientRect()
  const panelRect = panelEl.getBoundingClientRect()
  const stickyTop = Number.parseFloat(window.getComputedStyle(panelEl).top) || 0

  // Space from the panel's visible top to the bottom of the scrollport.
  const effectiveTop = Math.max(panelRect.top, parentRect.top + stickyTop)
  const fromRect = parentRect.bottom - effectiveTop

  // Cap using the scroll container's client height so nested CardBody scrollports stay correct.
  const fromClient = scrollParent.clientHeight - stickyTop - 16

  return Math.floor(Math.max(240, Math.min(fromRect, fromClient)))
}

/**
 * Max height for a sticky side panel inside a nested scroll container (e.g. CardBody).
 * Keeps the panel within the visible scrollport so internal overflow can scroll fully.
 */
export function useStickyPanelMaxHeight(ref: RefObject<HTMLElement | null>): number | undefined {
  const [maxHeight, setMaxHeight] = useState<number | undefined>(() => {
    if (typeof window === 'undefined') return undefined
    return Math.max(240, window.innerHeight - 160)
  })

  useLayoutEffect(() => {
    const panel = ref.current
    if (!panel) return

    const scrollParent = findScrollParent(panel)

    const measure = () => {
      const panelEl = ref.current
      if (!panelEl) return
      setMaxHeight(measureStickyPanelMaxHeight(panelEl, scrollParent))
    }

    measure()
    const rafId = window.requestAnimationFrame(measure)

    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(scrollParent)
    resizeObserver.observe(panel)

    window.addEventListener('resize', measure)
    scrollParent.addEventListener('scroll', measure, { passive: true })

    return () => {
      window.cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      window.removeEventListener('resize', measure)
      scrollParent.removeEventListener('scroll', measure)
    }
  }, [ref])

  return maxHeight
}
